import React from 'react';
import {
  Button,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  SxProps,
} from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ImageWrapper from '../../elements/ImageWrapper';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';

type ImageType = 'png' | 'jpg';
interface FeatureIconProps {
  cardText: string;
  fileName: string;
  parentFolder?: 'icons' | 'images' | undefined;
  imageType?: ImageType;
  onClick?: () => void;
  isDisabled?: boolean;
  rootStyles?: SxProps;
}

const styles: IStyles = {
  root: {
    backgroundColor: '#DBF0FF',
    height: { xs: 'auto', lg: pxTovW(75) },
    width: { xs: pxToRem(95), lg: pxTovW(355) },
    borderRadius: { xs: pxToRem(10), lg: pxTovW(50) },
    padding: {
      xs: pxToRem(8),
      lg: `${pxTovW(20)} ${pxTovW(15)}  ${pxTovW(18)} ${pxTovW(30)}`,
    },
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(15), md: pxTovW(15), lg: pxTovW(23) },
    justifyContent: { xs: 'center', lg: 'space-between' },
    boxSizing: 'border-box',
    border: '1px solid transparent', // default border for hover effect
    opacity: 1,
    transition: 'border-color 0.3s ease', // smooth transition

    '&:hover': {
      backgroundColor: '#DBF0FF',
      borderColor: '#D9D9D9', // change only border color on hover
    },
    marginX: 'auto',
  },
  image: {
    width: { xs: pxToRem(40), lg: pxTovW(45) },
    objectFit: 'cover',
    borderRadius: { xs: pxToRem(10), lg: pxTovW(10) },
  },
};

export function FeatureIconV2(props: FeatureIconProps) {
  const {
    cardText,
    fileName,
    parentFolder,
    imageType,
    onClick,
    isDisabled,
    rootStyles,
  } = props;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  let rootSx = { ...styles.root };
  if (rootStyles) rootSx = { ...rootSx, ...rootStyles };
  if (isDisabled)
    rootSx = {
      ...rootSx,
      opacity: 0.7,
      cursor: isDisabled ? 'default' : undefined,
    };

  return (
    <Button
      onClick={() => {
        if (isDisabled) {
          return;
        }
        if (onClick) onClick();
      }}
      sx={rootSx}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: 'center',
          gap: { xs: 1, lg: 3 },
        }}
      >
        <ImageWrapper
          name={fileName}
          type={imageType}
          parentFolder={parentFolder}
          styles={styles.image}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: '#212121',
            fontSize: { xs: pxToRem(12), lg: pxToRem(22) },
          }}
        >
          {cardText}
        </Typography>
      </Box>
      {!isMobile ? (
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: '50%',
            padding: pxToRem(4),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: pxToRem(35), lg: pxTovW(40) },
            height: { xs: pxToRem(35), lg: pxTovW(40) },
          }}
        >
          <ArrowForwardIosIcon
            sx={{
              fontSize: 16,
              color: '#212121',
            }}
          />
        </Box>
      ) : null}
    </Button>
  );
}

export default FeatureIconV2;
